import React from "react";
import { graphql, Link } from "gatsby";
import Logo from "../Logo";
import VisuallyHidden from "../VisuallyHidden";
import FancyLink from "../FancyLink";
import * as styles from "./header.module.css";

export const Header = ({ topMenu }) => {
  const [isMobileNavOpen, setIsMobileNavOpen] = React.useState(false);
  const [currentScheme, setCurrentScheme] = React.useState(null);
  const [isSticky, setIsSticky] = React.useState(false);

  function handleNavToggleClick(e) {
    e.preventDefault();
    setIsMobileNavOpen(prevState => !prevState);
  }

  const { menu_links, button_label, button_link } = topMenu || {};

  const menuLinks = menu_links
    ? menu_links.map((menuLink, index) => (
        <li key={`top-nav-${index}`}>
          <FancyLink href={menuLink.link.url} target={menuLink.link.target}>
            {menuLink.label.text}
          </FancyLink>
        </li>
      ))
    : null;

  React.useEffect(() => {
    if (isMobileNavOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.contains("overflow-hidden") &&
        document.body.classList.remove("overflow-hidden");
    }
  }, [isMobileNavOpen]);

  // Track scroll for sticky header
  React.useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track which section is in view and update button color
  React.useEffect(() => {
    const sections = document.querySelectorAll(
      "section[data-color-scheme], section[data-highlight-background]"
    );

    const observer = new IntersectionObserver(
      entries => {
        const visibleEntry = entries.find(entry => entry.isIntersecting);
        if (visibleEntry) {
          const scheme = visibleEntry.target.getAttribute("data-color-scheme");
          const highlightBg = visibleEntry.target.getAttribute(
            "data-highlight-background"
          );

          // Highlight attributes always override scheme
          if (highlightBg) {
            setCurrentScheme(`highlight-${highlightBg}`);
          } else if (scheme === "paper") {
            // Skip updating if paper scheme WITHOUT highlight overrides
            return;
          } else if (scheme) {
            setCurrentScheme(scheme);
          }
        }
      },
      {
        rootMargin: "-50% 0px -50% 0px",
        threshold: 0,
      }
    );

    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <header
      className={styles.container}
      data-menu-open={isMobileNavOpen}
      data-sticky={isSticky}
    >
      <div className={"page-width " + styles.wrapper}>
        <Link to="/" className={styles.logo}>
          <span className="visually-hidden">Navigate to home page</span>
          <span className={styles.logoIcon}>
            <Logo alt="Doing Things" variant="icon" color="white" />
          </span>
          <span className={styles.logoStacked}>
            <Logo alt="Doing Things" variant="stacked" color="white" />
          </span>
        </Link>
        {menuLinks && (
          <nav className={styles.nav} id="nav" aria-label="Main navigation">
            <ul>{menuLinks}</ul>
          </nav>
        )}
        {button_label && (
          <a
            className={styles.navBtn}
            href={button_link.url || "/#contact"}
            target={button_link.target}
            data-color-scheme={currentScheme}
          >
            <span>{button_label.text}</span>
          </a>
        )}
        <button
          className={styles.navToggle}
          aria-expanded={isMobileNavOpen}
          aria-controls="nav"
          onClick={handleNavToggleClick}
        >
          <span aria-hidden="true"></span>
          <VisuallyHidden>
            {(isMobileNavOpen ? "Close" : "Open") + " navigation"}
          </VisuallyHidden>
        </button>
      </div>
    </header>
  );
};

export const query = graphql`
  fragment TopMenuFragment on PrismicTopMenu {
    _previewable
    data {
      menu_links {
        label {
          text
        }
        link {
          id
          url
          target
        }
      }
      button_label {
        text
      }
      button_link {
        url
        target
      }
    }
  }
`;
